<script setup lang="ts">
import type {
  CollectedContainerGraphql,
  CollectGraphql,
  Maybe,
} from '~/types/graphql-backend-types/gql-types'
import {
  CodeIcon,
  NotebookPenIcon,
  ScaleIcon,
  TruckIcon,
} from 'lucide-vue-next'
import { useCollectsWithMissingFields } from '~/composables/collect/useCollectsWithMissingFields'
import { formatDateWithI18n } from '~/utils/date'

const { data: collectsWithMissingFields, isPending } = useCollectsWithMissingFields()
const { user } = storeToRefs(useAuthStore())

const { t } = useI18n()
const router = useRouter()

function formatDate(date: string | undefined): string {
  if (!date)
    return t('global.not_scheduled')

  return formatDateWithI18n(date, {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour12: false,
    timeZone: 'UTC',
  })
}

function getMissingFieldsCount(collect: CollectGraphql): number {
  let count = 0
  if (!collect.licensePlate)
    count++

  // Count missing fields in containers
  if (collect.collectedContainers?.collection) {
    collect.collectedContainers.collection.forEach((container: Maybe<CollectedContainerGraphql>) => {
      if (!container?.quantity)
        count++
      if (!container?.unit)
        count++
      if (!container?.treatmentCode)
        count++
    })
  }

  return count
}
</script>

<template>
  <div v-if="isPending" class="skeleton h-full w-full" />

  <Card v-else class="flex grow">
    <template #title>
      <NotebookPenIcon :size="24" />
      {{ t('catch_on.remaining_fill_collects', { count: collectsWithMissingFields?.length }) }}
      <div v-if="collectsWithMissingFields?.length" class="flex items-center gap-2 ml-auto">
        <EButton
          :label="t('global.see_all')"
          class="btn-primary btn-outline"
          size="sm"
          @click="router.push('/collects/catch-on')"
        />
      </div>
    </template>
    <template #content>
      <div class="rounded-lg flex flex-col gap-3">
        <div
          v-if="collectsWithMissingFields?.length === 0"
          class="bg-base-200 p-3 rounded-lg"
        >
          {{ user.client.hasCollects ? t('catch_on.no_collects_with_missing_fields') : t('catch_on.no_collects') }}
        </div>
        <div
          v-for="collect in collectsWithMissingFields.slice(0, 1)"
          :key="collect?.id"
          class="bg-base-200 p-3 rounded-lg"
        >
          <div class="flex justify-between gap-2 mb-2">
            <div class="font-semibold">
              {{ collect.client?.name || t('global.unknown_client') }}
            </div>
            <div class="text-sm text-base-content/70">
              {{ formatDate(collect?.startsAt) }}
            </div>
          </div>

          <div class="flex flex-wrap gap-2 mt-1">
            <div v-if="!collect.licensePlate" class="badge badge-sm badge-error gap-1">
              <TruckIcon :size="12" />
              {{ t('global.license_plate') }}
            </div>

            <template v-for="(container, index) in collect.collectedContainers?.collection || []" :key="index">
              <div v-if="!container.quantity" class="badge badge-sm badge-error gap-1">
                <ScaleIcon :size="12" />
                {{ t('global.quantity') }}
              </div>
              <div v-if="!container.treatmentCode" class="badge badge-sm badge-error gap-1">
                <CodeIcon :size="12" />
                {{ t('global.treatment_code') }}
              </div>
            </template>
          </div>

          <div class="text-xs text-base-content/70 mt-2">
            {{ getMissingFieldsCount(collect) }} {{ t('catch_on.fields_to_complete') }}
          </div>
        </div>
      </div>
    </template>
  </Card>
</template>
